import { format } from 'date-fns';
import EventBus from 'eventing-bus';
import Switch from '@mui/material/Switch';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Edit as EditIcon, ContentCopy as CopyIcon, Download as DownloadIcon, LocalOffer as TagIcon, Add as AddIcon } from '@mui/icons-material';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Box, TextField, Typography, Paper, Select, MenuItem, FormControl, Chip, Alert, CircularProgress, Tabs, Tab, Tooltip, IconButton, Popover } from '@mui/material';
import { getSingleWorkflow, createWorkflow, updateWorkflow, deleteWorkflow, setWorkflowNodes, setWorkflowEdges, executeWorkflow, getExecutionDetails, getWorkflowExecutions, cloneWorkflow } from '../../store/Workflows';
import { getNodeTools } from '../../store/Integration';
import { getAllObjectTags } from '../../store/Objects';
import TagsManager from '../../components/Tags/TagsManager';

import WorkflowDetail from './WorkflowDetail';
import { getWorkspacePath } from '../../utils/workspaceUtils';
import { validateWorkflow } from '../../utils/workflowValidator';
import ReactFlowWorkflowBuilder from './WorkflowBuilder/ReactFlowBuilder';
import CollaborativePresence from '../../components/CollaborativePresence';
import PlanAnimation from '../../components/lottiefilesAnimation/PlanAnimation.js';


// Utility function to process decision node edges
const processDecisionEdges = (edges, nodes, logPrefix = '') => {
  return edges.map(edge => {
    const sourceNode = nodes.find(n => n.id === edge.source);
    const isDecisionNode = sourceNode?.type === 'decision';
    
    if (isDecisionNode) {
      let updatedEdge = { ...edge };
      
      console.log(`[${logPrefix}] Processing edge: id=${edge.id}, source=${edge.source}, target=${edge.target}, sourceHandle=${edge.sourceHandle}, type=${edge.type}, isDecisionNode=${isDecisionNode}`);
      
      // Ensure decision node edges have type='conditional'
      if (!edge.type || edge.type !== 'conditional') {
        console.log(`[${logPrefix}] Setting edge ${edge.id} type to 'conditional' for decision node`);
        updatedEdge.type = 'conditional';
      }
      
      // Assign sourceHandle if missing (but preserve 'false' string values)
      if (edge.sourceHandle === null || edge.sourceHandle === undefined) {
        console.warn(`[${logPrefix}] Decision edge ${edge.id} missing sourceHandle - assigning based on edge order`);
        
        // Find all edges from this decision node
        const allEdgesFromDecision = edges.filter(e => e.source === edge.source);
        
        // Sort edges consistently (by creation order based on edge ID, then by target)
        const sortedEdges = [...allEdgesFromDecision].sort((a, b) => {
          const aNum = parseInt(a.id.replace(/\D/g, '')) || 0;
          const bNum = parseInt(b.id.replace(/\D/g, '')) || 0;
          
          if (aNum !== bNum) {
            return aNum - bNum; // Earlier created edge gets 'true'
          }
          
          return a.target.localeCompare(b.target);
        });
        
        const edgeIndex = sortedEdges.findIndex(e => e.id === edge.id);
        updatedEdge.sourceHandle = edgeIndex === 0 ? 'true' : 'false';
        
        console.log(`[${logPrefix}] Assigned sourceHandle '${updatedEdge.sourceHandle}' to edge ${edge.id} (index: ${edgeIndex})`);
      } else {
        console.log(`[${logPrefix}] Decision edge ${edge.id} has preserved sourceHandle: ${edge.sourceHandle} (type: ${typeof edge.sourceHandle})`);
      }
      
      return updatedEdge;
    }
    
    return edge;
  });
};

const WorkflowBuilder = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [currentWorkflowId, setCurrentWorkflowId] = useState(id);
  // Edit mode is true if we have an ID (from URL or after creation)
  const isEditMode = !!(id || currentWorkflowId);

  const [checked, setChecked] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [advanceSetting, setAdvanceSetting] = useState(false);
  // const inputRef = useRef(null);

  const { singleWorkflow, singleWorkflowLoading, workflowNodes, workflowEdges, executionDetails, executions } = useSelector(state => state['Workflows']);
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const { nodeTools } = useSelector(state => state.Integration);

  const [workflowData, setWorkflowData] = useState({
    name: '',
    type: 'custom',
    description: '',
    status: 'inactive',
    settings: {
      errorHandling: 'stop',
      maxExecutionTime: 3600000,
      retryPolicy: { maxRetries: 3, retryDelay: 60000 },
      executionLogRetention: { maxLogs: 10, logType: 'default' }
    }
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState('visual'); // 'visual' or 'json'
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastExecutionId, setLastExecutionId] = useState(null);
  const [latestExecution, setLatestExecution] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loadingExecution, setLoadingExecution] = useState(false);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneName, setCloneName] = useState(`${singleWorkflow.name} (Copy)`);
  const [tagsAnchorEl, setTagsAnchorEl] = useState(null);
  const tagsOpen = Boolean(tagsAnchorEl);

  // Function to fetch latest execution
  const fetchLatestExecution = async () => {
    const workflowId = id || currentWorkflowId;
    if (!workflowId || !isEditMode || workflowId === 'undefined') return;
    try {
      setLoadingExecution(true);
      // Fetch executions using the correct format
      await dispatch(getWorkflowExecutions({ workflowId: workflowId }));
    } catch (error) {
      console.error('Failed to fetch latest execution:', error);
    } finally {
      setLoadingExecution(false);
    }
  };

  // Load node tools on component mount
  useEffect(() => {
    if (!nodeTools.loading && !nodeTools.fetched) {
      dispatch(getNodeTools());
    }
  }, [dispatch, nodeTools.loading, nodeTools.fetched]);

  useEffect(() => {
    if (isEditMode) {
      const workflowId = id || currentWorkflowId;
      if (workflowId) {
        dispatch(getSingleWorkflow(workflowId));
        setIsInitialized(false); // Reset initialization when workflow changes
        // Fetch latest execution data for the workflow
        fetchLatestExecution();
      }
    } else {
      // Initialize empty workflow for new workflows
      dispatch(setWorkflowNodes([]));
      dispatch(setWorkflowEdges([]));
    }
  }, [dispatch, id, currentWorkflowId, isEditMode]);

  useEffect(() => {
    console.log(`[WorkflowBuilder] useEffect triggered - isEditMode: ${isEditMode}, singleWorkflow._id: ${singleWorkflow['_id']}, isInitialized: ${isInitialized}, nodeTools.data.length: ${nodeTools.data?.length}`);
    if (isEditMode && singleWorkflow['_id'] && nodeTools.data && nodeTools.data.length > 0) {
      console.log(`[WorkflowBuilder] Starting workflow initialization...`);
      
      // Only set workflow data if not already initialized
      if (!isInitialized) {
        setWorkflowData({
          name: singleWorkflow['name'] || '',
          type: singleWorkflow['type'] || 'custom',
          status: singleWorkflow['status'] || 'inactive',
          description: singleWorkflow['description'] || '',
          tags: singleWorkflow['tags'] || [],
          settings: singleWorkflow['settings'] || workflowData['settings']
        });
        setIsInitialized(true);
      }

      // Transform nodes from backend structure to frontend structure
      const transformedNodes = (singleWorkflow['nodes'] || []).map(node => {
        let nodeConfig = {};

        // Extract config from wherever it exists
        if (node['config'] && typeof node['config'] === 'object') nodeConfig = node['config']
        else if (node['data']?.config && typeof node['data']['config'] === 'object') nodeConfig = node['data']['config']


        // Log communication nodes with call channel
        if (node['type'] === 'communication' && nodeConfig['channel'] === 'call') {
        }

        // Ensure trigger nodes have required config - but preserve existing configuration
        if (node['type'] === 'trigger') {
          // Sync type and triggerType fields if one exists but not the other
          if (nodeConfig['triggerType'] && !nodeConfig['type']) {
            nodeConfig = { ...nodeConfig, type: nodeConfig['triggerType'] }
            console.log('[WorkflowBuilder] Syncing type from triggerType during load:', node.id, nodeConfig['triggerType']);
          } else if (nodeConfig['type'] && !nodeConfig['triggerType']) {
            nodeConfig = { ...nodeConfig, triggerType: nodeConfig['type'] }
            console.log('[WorkflowBuilder] Syncing triggerType from type during load:', node.id, nodeConfig['type']);
          }
          // Only set manual if no type is configured
          else if (!nodeConfig['type'] && !nodeConfig['triggerType']) {
            console.warn('[WorkflowBuilder] Setting default trigger type to manual for node:', node.id);
            nodeConfig = { ...nodeConfig, type: 'manual', triggerType: 'manual' }
          } else {
            console.log('[WorkflowBuilder] Trigger node config preserved:', node.id, nodeConfig);
          }

          // Only auto-detect schedule type if no explicit triggerType is set
          if (!nodeConfig['type'] && !nodeConfig['triggerType']) {
            // Check if it's a schedule trigger by looking for schedule-specific fields
            if (nodeConfig['scheduleType'] || nodeConfig['interval'] || nodeConfig['cronExpression']) {
              nodeConfig = { ...nodeConfig, type: 'schedule', triggerType: 'schedule' }
              console.log('[WorkflowBuilder] Auto-detected schedule trigger during load:', node.id, nodeConfig);
            }
            // Check for nested schedule object with meaningful data
            else if (nodeConfig['schedule'] && typeof nodeConfig['schedule'] === 'object' &&
              (nodeConfig['schedule'].type || nodeConfig['schedule'].interval || nodeConfig['schedule'].cronExpression)) {
              nodeConfig = { ...nodeConfig, type: 'schedule', triggerType: 'schedule' }
              console.log('[WorkflowBuilder] Auto-detected nested schedule configuration during load:', node.id, nodeConfig['schedule']);
            }
          } else {
            console.log('[WorkflowBuilder] Preserving explicit trigger type during load:', node.id, 'type:', nodeConfig['triggerType'] || nodeConfig['type']);
          }
        }

        // Ensure CRM nodes have required config
        if (node['type'] === 'crm' && !nodeConfig['operation']) nodeConfig = { ...nodeConfig, operation: 'get', entity: 'contact' }

        // Try to find nodeDefinition from nodeTools if missing
        let nodeDefinition = node['data']?.nodeDefinition;
        let iconSvg = node['data']?.iconSvg;

        // If nodeDefinition is missing, try to get it from nodeTools
        if (!nodeDefinition && nodeTools.data && nodeTools.data.length > 0) {
          const nodeTemplate = nodeTools.data.find(tool =>
            tool.nodeDefinition?.nodeType === node['type']
          );

          if (nodeTemplate) {
            nodeDefinition = nodeTemplate.nodeDefinition;
            iconSvg = nodeTemplate.iconSvg || iconSvg;
            console.log('[WorkflowBuilder] Found nodeDefinition from nodeTools for:', node.type);
          }
        }

        const transformedNode = {
          ...node,
          data: {
            ...node['data'], // This preserves nodeDefinition from backend
            config: nodeConfig,
            label: node['name'] || node['data']?.label || 'Unnamed Node',
            // Explicitly preserve nodeDefinition and iconSvg from backend
            nodeDefinition: nodeDefinition,
            iconSvg: iconSvg
          },
          // Remove config from node level as it's now in data.config
          config: undefined
        };

        // Log transformed communication nodes
        if (node['type'] === 'communication' && nodeConfig['channel'] === 'call') {
        }

        return transformedNode;
      })

      dispatch(setWorkflowNodes(transformedNodes))
      
      // Process edges to ensure decision node edges have correct type and handle preservation
      const processedEdges = processDecisionEdges(singleWorkflow['edges'] || [], transformedNodes, 'WorkflowBuilder');
      
      console.log(`[WorkflowBuilder] Processed ${processedEdges.length} edges, ${processedEdges.filter(e => e.type === 'conditional').length} decision edges`);
      dispatch(setWorkflowEdges(processedEdges))
    }
  }, [singleWorkflow, isEditMode, isInitialized, nodeTools, dispatch])

  // Function to update individual node execution status in real-time
  const updateNodeExecutionStatus = (nodeId, status, output) => {
    dispatch(setWorkflowNodes(workflowNodes.map(node => node['id'] === nodeId ? { ...node, data: { ...node['data'], executionStatus: status, lastExecuted: new Date(), output: output || node['data']['output'] } } : node)))
  }

  // WebSocket integration for real-time execution updates
  useEffect(() => {
    const workflowId = id || currentWorkflowId;

    // Node-level execution events
    const handleNodeExecutionStart = (data) => {
      if (data['workflowId'] === workflowId && data['nodeId']) updateNodeExecutionStatus(data['nodeId'], 'running', null)
    }

    const handleNodeExecutionComplete = (data) => {
      if (data['workflowId'] === workflowId && data['nodeId']) updateNodeExecutionStatus(data['nodeId'], 'completed', data['output'])
    }

    const handleNodeExecutionError = (data) => {
      if (data['workflowId'] === workflowId && data['nodeId']) updateNodeExecutionStatus(data['nodeId'], 'failed', data['error'])
    }

    // Workflow-level execution events
    const handleExecutionComplete = (data) => { if (data['workflowId'] === workflowId) fetchLatestExecution() }

    const handleExecutionFailed = (data) => { if (data['workflowId'] === workflowId) fetchLatestExecution() }

    const handleExecutionUpdate = (data) => { if (data['workflowId'] === workflowId) { } }

    // Listen to node-level events
    const unsubNodeStart = EventBus.on('node:start', handleNodeExecutionStart);
    const unsubNodeComplete = EventBus.on('node:complete', handleNodeExecutionComplete);
    const unsubNodeError = EventBus.on('node:error', handleNodeExecutionError);

    // Listen to workflow-level events
    const unsubExecComplete = EventBus.on('workflow:execution:completed', handleExecutionComplete);
    const unsubExecFailed = EventBus.on('workflow:execution:failed', handleExecutionFailed);
    const unsubExecUpdate = EventBus.on('workflow:execution:update', handleExecutionUpdate);

    return () => {
      unsubNodeStart?.();
      unsubNodeComplete?.();
      unsubNodeError?.();
      unsubExecComplete?.();
      unsubExecFailed?.();
      unsubExecUpdate?.();
    };
  }, [id, currentWorkflowId, updateNodeExecutionStatus]);

  // Process executions when they're loaded
  useEffect(() => {
    if (executions && executions.length > 0 && !latestExecution) {
      const latestExecutionId = executions[0]._id;
      dispatch(getExecutionDetails(latestExecutionId));
    }
  }, [executions, dispatch, latestExecution]);

  // Update latestExecution when execution details are loaded
  useEffect(() => {
    if (executionDetails && executionDetails._id && executions && executions.length > 0) {
      if (executionDetails._id === executions[0]._id) {
        setLatestExecution(executionDetails);
      }
    }
  }, [executionDetails, executions]);

  // Watch for execution details updates
  useEffect(() => {
    if (lastExecutionId && executionDetails && executionDetails._id === lastExecutionId) {
      const executedNodes = executionDetails.executedNodes || [];

      // Transform executedNodes array into nodeOutputs map
      const nodeOutputsMap = {};
      executedNodes.forEach(executedNode => {
        if (executedNode.nodeId && executedNode.output !== undefined) {
          nodeOutputsMap[executedNode.nodeId] = executedNode.output;
        }
      });

      // Use nodeOutputs from response or build from executedNodes
      const nodeOutputs = executionDetails.nodeOutputs || nodeOutputsMap;


      // Update nodes with actual execution status
      const updatedNodes = workflowNodes.map(node => {
        const executedNode = executedNodes.find(en => en.nodeId === node.id);
        const nodeOutput = nodeOutputs[node.id];


        return {
          ...node,
          data: {
            ...node.data,
            output: executedNode?.output || nodeOutput || null,
            raw: executedNode?.raw || executedNode?.output || nodeOutput || null,
            executionStatus: executedNode ? executedNode.status : 'pending',
            error: executedNode?.error || null
          }
        };
      });

      dispatch(setWorkflowNodes(updatedNodes));
      setLastExecutionId(null); // Reset to prevent re-triggering
    }
  }, [executionDetails, lastExecutionId, workflowNodes, dispatch]);

  // WebSocket event listeners - commented out as WebSockets are not working
  // useEffect(() => {
  //   if (!currentExecutionId) return;
  //   // ... WebSocket implementation
  // }, [currentExecutionId, workflowNodes, dispatch]);

  const handleChange = (field, value) => {
    setWorkflowData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleTagsClick = (event) => {
    setTagsAnchorEl(event.currentTarget);
    dispatch(getAllObjectTags({ objectType: 'workflows' }));
  };

  const handleTagsClose = () => {
    setTagsAnchorEl(null);
  };

  const handleTagsApplied = (mergedTags) => {
    // Immediately update the local state with merged tags
    if (mergedTags) {
      setWorkflowData(prev => ({ ...prev, tags: mergedTags }));
      setIsDirty(true);

      // If in edit mode, also update Redux store for the main list
      if (isEditMode && singleWorkflow) {
        const updatedWorkflow = {
          ...singleWorkflow,
          tags: mergedTags
        };
        dispatch({ type: 'workflows/UPDATE_WORKFLOW_IN_LIST', payload: updatedWorkflow });
      }
    }
    // Also refresh from server if in edit mode
    if (isEditMode) {
      const workflowId = id || currentWorkflowId;
      dispatch(getSingleWorkflow(workflowId));
    }
    handleTagsClose();
  };

  // Tag removal functionality has been disabled
  // const handleRemoveTag = (tagToRemove) => {
  //   const updatedTags = workflowData.tags.filter(tag => tag.name !== tagToRemove.name);
  //   // ... removal logic
  // };

  const handleSettingChange = (setting, value) => {
    setWorkflowData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [setting]: value
      }
    }));
  };

  const validateForm = () => {
    const validation = validateWorkflow({
      name: workflowData.name,
      nodes: workflowNodes,
      edges: workflowEdges,
      status: workflowData.status
    });

    console.log('Validation result:', validation);
    console.log('Validation errors:', validation.errors);
    console.log('Validation warnings:', validation.warnings);

    const newErrors = {};

    // Convert validation errors to form errors
    validation.errors.forEach(error => {
      if (error.field) {
        newErrors[error.field] = error.message;
      } else if (error.nodeId) {
        newErrors.nodes = error.message;
      }
    });

    // Show first validation error as general error
    if (validation.errors.length > 0 && !Object.keys(newErrors).length) {
      newErrors.submit = validation.errors[0].message;
    }

    setErrors(newErrors);
    setWarnings(validation.warnings);

    return validation.isValid;
  };

  const handleSave = () => {
    console.log('handleSave called', { workflowData, isEditMode });

    // For new workflows, ensure we have a name - set default if empty
    let nameToUse = workflowData.name;
    if (!nameToUse || nameToUse.trim() === '') {
      nameToUse = isEditMode ? (singleWorkflow?.name || 'Untitled Workflow') : 'My workflow';
      // Update the workflow data with the default name
      setWorkflowData(prev => ({ ...prev, name: nameToUse }));
    }

    // FIRST: Process nodes to ensure they have required configuration
    const processedNodes = workflowNodes.map(node => {
      const processedNode = { ...node };

      // Ensure trigger nodes have required config
      if (node.type === 'trigger') {
        const currentConfig = node.data?.config || {};

        // If triggerType is already explicitly set, preserve it and sync both fields
        if (currentConfig.type || currentConfig.triggerType) {
          const triggerValue = currentConfig.triggerType || currentConfig.type;
          processedNode.data = {
            ...processedNode.data,
            config: {
              ...currentConfig,
              type: triggerValue,
              triggerType: triggerValue
            }
          };
          console.log('Preserving explicit trigger type:', processedNode.id, 'type:', triggerValue);
        }
        // Auto-detect schedule type if schedule fields are present
        else if (currentConfig.scheduleType || currentConfig.interval || currentConfig.cronExpression) {
          processedNode.data = {
            ...processedNode.data,
            config: {
              ...currentConfig,
              type: 'schedule',
              triggerType: 'schedule'
            }
          };
          console.log('Auto-detected schedule trigger type for node:', processedNode.id);
        }
        // Check for nested schedule object with meaningful data
        else if (currentConfig.schedule && typeof currentConfig.schedule === 'object' &&
          (currentConfig.schedule.type || currentConfig.schedule.interval || currentConfig.schedule.cronExpression)) {
          processedNode.data = {
            ...processedNode.data,
            config: {
              ...currentConfig,
              type: 'schedule',
              triggerType: 'schedule'
            }
          };
          console.log('Auto-detected nested schedule configuration:', processedNode.id, currentConfig.schedule);
        }
        // Default to manual only if no type can be determined
        else {
          processedNode.data = {
            ...processedNode.data,
            config: {
              ...currentConfig,
              type: 'manual',
              triggerType: 'manual'
            }
          };
          console.log('Defaulting to manual trigger type for node:', processedNode.id);
        }
      }

      // Ensure CRM nodes have required config  
      if (node.type === 'crm') {
        const currentConfig = node.data?.config || {};
        if (!currentConfig.operation) {
          processedNode.data = {
            ...processedNode.data,
            config: {
              ...currentConfig,
              operation: 'get',
              entity: 'contact'
            }
          };
          console.log('Fixed CRM node config:', processedNode);
        }
      }

      return processedNode;
    });

    // Update the Redux store with processed nodes so UI shows correct config
    dispatch(setWorkflowNodes(processedNodes));

    // SECOND: Process edges to ensure decision node edges have correct type and sourceHandle
    const processedEdges = processDecisionEdges(workflowEdges, processedNodes, 'handleSave');

    console.log(`[handleSave] Processed ${processedEdges.length} edges, ${processedEdges.filter(e => e.type === 'conditional').length} decision edges`);

    // Update the Redux store with processed edges
    dispatch(setWorkflowEdges(processedEdges));

    // Create a temporary workflow data object for validation with the correct name
    const tempWorkflowData = { ...workflowData, name: nameToUse };

    // THEN: Validate with the processed nodes and edges
    const validation = validateWorkflow({
      name: tempWorkflowData.name,
      nodes: processedNodes, // Use processed nodes for validation
      edges: processedEdges, // Use processed edges for validation
      status: tempWorkflowData.status
    });

    console.log('Validation result:', validation);

    if (!validation.isValid) {
      const newErrors = {};
      validation.errors.forEach(error => {
        if (error.field) {
          newErrors[error.field] = error.message;
        } else if (error.nodeId) {
          newErrors.nodes = error.message;
        }
      });

      if (validation.errors.length > 0 && !Object.keys(newErrors).length) {
        newErrors.submit = validation.errors[0].message;
      }

      setErrors(newErrors);
      setWarnings(validation.warnings);
      return;
    }

    setSaving(true);
    setSaveSuccess(false); // Reset success message

    // Log communication nodes being saved
    processedNodes.forEach(node => {
      if (node.type === 'communication' && node.data?.config?.channel === 'call') {
        console.log(`[WorkflowBuilder - handleSave] 📞 Communication node ${node.id}:`, node.data?.config);
        console.log(`[WorkflowBuilder - handleSave] 🎯 dynamicVariables:`, node.data?.config?.dynamicVariables);
      }
    });

    const data = {
      ...workflowData,
      name: nameToUse, // Use the name we determined
      nodes: processedNodes, // Use the processed nodes with fixed config
      edges: processedEdges // Use the processed edges with correct sourceHandle and type
    };

    const successCallback = (workflow) => {
      console.log('Save success callback', { workflow, isEditMode });
      setSaving(false);
      if (!isEditMode && workflow._id) {
        // For new workflows, update the URL and set the workflow ID
        const newUrl = getWorkspacePath(`/agent/workflows/${workflow._id}/edit`, currentWorkspace?._id);
        console.log('Updating URL to:', newUrl);
        window.history.replaceState(null, '', newUrl);
        setCurrentWorkflowId(workflow._id);
      }

      // Update local state with the saved workflow data
      setWorkflowData({
        name: workflow['name'] || workflowData['name'],
        type: workflow['type'] || workflowData['type'],
        status: workflow['status'] || workflowData['status'],
        settings: workflow['settings'] || workflowData['settings'],
        description: workflow['description'] || workflowData['description'],
      });
      setSaveSuccess(true);
      setIsDirty(false); // Reset dirty flag on successful save
      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    }

    const failCallback = (error) => {
      console.error('Save error callback', { error, isEditMode });
      setSaving(false);
      setErrors({ submit: error?.response?.data?.message || 'Failed to save workflow' });
    }

    console.log('About to dispatch workflow action', { isEditMode, id, currentWorkflowId, data });

    if (isEditMode) {
      const workflowId = id || currentWorkflowId;
      console.log('Dispatching updateWorkflow with ID:', workflowId);
      dispatch(updateWorkflow(workflowId, data, successCallback, failCallback));
    } else {
      console.log('Dispatching createWorkflow');
      dispatch(createWorkflow({ data, successCallback, failCallback }));
    }
  };

  const handleTestRun = () => {
    if (!validateForm()) return;

    // Save workflow first if not saved
    if (!isEditMode) {
      setErrors({ submit: 'Please save the workflow first before testing' });
      return;
    }


    // Mark all nodes as starting execution
    const startingNodes = workflowNodes.map(node => ({ ...node, data: { ...node['data'], output: null, executionStatus: 'pending' } }));
    dispatch(setWorkflowNodes(startingNodes));

    // Execute the workflow
    const workflowId = id || currentWorkflowId;
    dispatch(executeWorkflow({
      id: workflowId,
      input: {},
      options: { forceRun: true }, // Allow running draft workflows for testing
      successCallback: (result) => {
        // Extract execution ID and node outputs from the result
        if (result && result['body']) {
          const executionResult = result['body'];
          const executionId = executionResult.executionId || executionResult._id;

          // Build nodeOutputs from executedNodes if not present
          let nodeOutputs = executionResult['nodeOutputs'] || {};

          // If nodeOutputs is empty but executedNodes exists, build it
          if ((!nodeOutputs || Object.keys(nodeOutputs).length === 0) && executionResult['executedNodes']) {
            executionResult['executedNodes'].forEach(executedNode => {
              if (executedNode['nodeId'] && executedNode['output'] !== undefined)
                nodeOutputs[executedNode['nodeId']] = executedNode['output'];
            });
          }

          if (executionId) {
            // Store the execution ID and fetch details
            setLastExecutionId(executionId);
            dispatch(getExecutionDetails(executionId));

            // Provide immediate feedback with available outputs
            const updatedNodes = workflowNodes.map(node => {
              const nodeOutput = nodeOutputs[node['id']];

              // Check if the output indicates an error
              const hasError = nodeOutput && nodeOutput['error'] === true;

              return {
                ...node,
                data: {
                  ...node['data'],
                  raw: nodeOutput || null,
                  output: nodeOutput || null,
                  error: hasError ? nodeOutput : null,
                  executionStatus: hasError ? 'failed' : (nodeOutput !== undefined ? 'completed' : 'pending'),
                }
              };
            });
            dispatch(setWorkflowNodes(updatedNodes));
          }
        }
      },
      failCallback: (error) => {
        setErrors({ submit: error?.['response']?.['data']?.['message'] || 'Failed to execute workflow' });

        // Only mark the failed node as error, not all nodes
        const errorNodeId = error?.['response']?.['data']?.['body']?.['nodeId'] || error?.['nodeId'];
        const updatedNodes = workflowNodes.map(node => {
          if (errorNodeId && node.id === errorNodeId) {
            return {
              ...node,
              data: {
                ...node['data'],
                executionStatus: 'error',
                error: error?.['response']?.['data']?.['body'] || error
              }
            };
          }
          // Keep other nodes in their current state
          return node;
        });

        dispatch(setWorkflowNodes(updatedNodes));
      }
    }));
  };

  const handleDelete = () => {
    const workflowId = id || currentWorkflowId;
    dispatch(deleteWorkflow(workflowId));
    // The saga will handle navigation and success/error messages
  };

  if (singleWorkflowLoading && isEditMode)
    return <Box className="plan-animation-overlay" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}> <PlanAnimation /> </Box>

  const getStatusColor = (status) => {
    const statusColors = {
      failed: 'error',
      running: 'info',
      active: 'success',
      inactive: 'default',
      completed: 'success',
    };
    return statusColors[status] || 'default';
  };

  const handleClone = () => {
    const workflowId = id || currentWorkflowId;
    if (cloneName?.trim()) {
      // Show loading state
      setLoadingExecution(true);

      dispatch(cloneWorkflow({
        id: workflowId,
        name: cloneName,
        successCallback: (clonedWorkflow) => {
          setLoadingExecution(false);
          setCloneDialogOpen(false);
        },
        failCallback: (error) => {
          setLoadingExecution(false);
        }
      }));
    }
  };

  const handleOpenCloneDialog = () => {
    // Set the clone name to current workflow name + (Copy)
    setCloneName(`${singleWorkflow.name} (Copy)`);
    setCloneDialogOpen(true);
  };

  const handleDownloadJSON = () => {
    // Create workflow export object with all metadata
    const workflowExport = {
      name: workflowData.name || 'Untitled Workflow',
      description: workflowData.description,
      type: workflowData.type,
      status: workflowData.status,
      settings: workflowData.settings,
      nodes: workflowNodes,
      edges: workflowEdges,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };

    // Convert to formatted JSON string
    const jsonString = JSON.stringify(workflowExport, null, 2);

    // Create blob and download
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    // Generate filename with sanitized workflow name and timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const safeName = workflowData.name?.replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'untitled';
    const filename = `workflow-${safeName}-${timestamp}.json`;

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
  };

  // const handleDelete = () => {
  //   dispatch(deleteWorkflow(id));
  //   setDeleteDialogOpen(false);
  // };

  // Detect outside click
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (inputRef.current && !inputRef.current.contains(event.target)) {
  //       setShowInput(false);
  //     }
  //   };

  //   if (showInput) {
  //     document.addEventListener('mousedown', handleClickOutside);
  //   } else {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   }

  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, [showInput]);

  return (
    <>
      <div className='single-agent-page workflow-create-page'>
        {/* ========== Header Section ========== */}
        <div className='page-header-new style-two style-workflow-header'>
          <div className='title-area-top'>
            <div className='left-area-top'>
              <h2>
                <button onClick={() => history.push(getWorkspacePath('/agent/workflows', currentWorkspace?._id))} className='back-btn' >
                  <svg width='8' height='14' viewBox='0 0 8 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M7 13L1 7L7 1' stroke='black' strokeWidth='2' stroke-linecap='round' stroke-linejoin='round' />
                  </svg>
                  Back
                </button>
              </h2>
            </div>
            <div className='center-title'>
              <h2>
                <div className='right-heading-area'>
                  {isEditing ?
                    <input
                      autoFocus
                      type='text'
                      name='name'
                      className='editable-input'
                      value={workflowData['name'] || ''}
                      onBlur={() => { setIsEditing(false); }}
                      onChange={(e) => handleChange('name', e.target.value)}
                    />
                    : <span className='agent-name' onClick={() => setIsEditing(true)}> {workflowData['name'] || (isEditMode ? singleWorkflow['name'] : "My workflow")} </span>
                  }
                </div>
                {/* Tags Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                  <button className='add-tab' onClick={handleTagsClick}>Add Tag</button>
                  {workflowData.tags && workflowData.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {workflowData.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag.name}
                          size="small"
                          sx={{
                            backgroundColor: tag.color || '#6B7280',
                            color: 'white'
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  {/* <IconButton
                    size="small"
                    onClick={handleTagsClick}
                    sx={{
                      backgroundColor: 'rgba(107, 114, 128, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(107, 114, 128, 0.2)'
                      }
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton> */}
                </Box>
              </h2>
            </div>
            {/* View Mode Tabs */}
            {/* <div className='tabs-header-workflow'>
              <Tabs value={viewMode} onChange={(e, value) => setViewMode(value)}>
                <Tab label="Visual" value="visual" icon={<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 8.375H4C3.795 8.375 3.625 8.205 3.625 8V4C3.625 3.795 3.795 3.625 4 3.625H8C8.205 3.625 8.375 3.795 8.375 4V8C8.375 8.205 8.205 8.375 8 8.375ZM4.375 7.625H7.625V4.375H4.375V7.625Z" fill="#4B5563" stroke="#4B5563" stroke-width="0.2" /><path d="M2.5 11.375C1.465 11.375 0.625 10.535 0.625 9.5C0.625 8.465 1.465 7.625 2.5 7.625H4C4.205 7.625 4.375 7.795 4.375 8V9.5C4.375 10.535 3.535 11.375 2.5 11.375ZM2.5 8.375C1.88 8.375 1.375 8.88 1.375 9.5C1.375 10.12 1.88 10.625 2.5 10.625C3.12 10.625 3.625 10.12 3.625 9.5V8.375H2.5Z" fill="#4B5563" stroke="#4B5563" stroke-width="0.2" /><path d="M4 4.375H2.5C1.465 4.375 0.625 3.535 0.625 2.5C0.625 1.465 1.465 0.625 2.5 0.625C3.535 0.625 4.375 1.465 4.375 2.5V4C4.375 4.205 4.205 4.375 4 4.375ZM2.5 1.375C1.88 1.375 1.375 1.88 1.375 2.5C1.375 3.12 1.88 3.625 2.5 3.625H3.625V2.5C3.625 1.88 3.12 1.375 2.5 1.375Z" fill="#4B5563" stroke="#4B5563" stroke-width="0.2" /><path d="M9.5 4.375H8C7.795 4.375 7.625 4.205 7.625 4V2.5C7.625 1.465 8.465 0.625 9.5 0.625C10.535 0.625 11.375 1.465 11.375 2.5C11.375 3.535 10.535 4.375 9.5 4.375ZM8.375 3.625H9.5C10.12 3.625 10.625 3.12 10.625 2.5C10.625 1.88 10.12 1.375 9.5 1.375C8.88 1.375 8.375 1.88 8.375 2.5V3.625Z" fill="#4B5563" stroke="#4B5563" stroke-width="0.2" /><path d="M9.5 11.375C8.465 11.375 7.625 10.535 7.625 9.5V8C7.625 7.795 7.795 7.625 8 7.625H9.5C10.535 7.625 11.375 8.465 11.375 9.5C11.375 10.535 10.535 11.375 9.5 11.375ZM8.375 8.375V9.5C8.375 10.12 8.88 10.625 9.5 10.625C10.12 10.625 10.625 10.12 10.625 9.5C10.625 8.88 10.12 8.375 9.5 8.375H8.375Z" fill="#4B5563" stroke="#4B5563" stroke-width="0.2" /></svg>} iconPosition="start" />
                <Tab label="JSON" value="json" icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.56036 6.57593C9.90359 6.23271 10.4759 6.23271 10.8192 6.57593C11.1623 6.91916 11.1623 7.49152 10.8192 7.83472L6.86603 11.7869L6.65411 11.9988L6.86603 12.2107L10.8192 16.1794V16.1804C11.1621 16.5234 11.1616 17.094 10.8192 17.4373C10.6317 17.6092 10.4051 17.6999 10.1902 17.7C9.96389 17.7 9.73926 17.6171 9.56036 17.4382L5.60822 13.4861C4.78874 12.6666 4.78874 11.3485 5.60822 10.5291L9.56036 6.57593Z" fill="#4B5563" stroke="white" stroke-width="0.6" /><path d="M15.181 6.56006C15.5243 6.21684 16.0966 6.21684 16.4398 6.56006L20.392 10.5132C21.2114 11.3327 21.2114 12.6508 20.392 13.4702L16.4398 17.4224H16.4388L16.432 17.4302C16.269 17.6049 16.0321 17.6997 15.8099 17.6997C15.5837 17.6997 15.3599 17.6177 15.181 17.439C14.8593 17.1173 14.8392 16.5945 15.1205 16.2476L15.181 16.1802L19.1342 12.2114L19.3451 11.9995L19.1342 11.7876L15.181 7.81885C14.8378 7.47563 14.8378 6.90328 15.181 6.56006Z" fill="#4B5563" stroke="white" stroke-width="0.6" /></svg>} iconPosition="start" />
                <Tab label="Executions" value="executions" icon={<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 8.375H4C3.795 8.375 3.625 8.205 3.625 8V4C3.625 3.795 3.795 3.625 4 3.625H8C8.205 3.625 8.375 3.795 8.375 4V8C8.375 8.205 8.205 8.375 8 8.375ZM4.375 7.625H7.625V4.375H4.375V7.625Z" fill="#4B5563" stroke="#4B5563" stroke-width="0.2" /><path d="M2.5 11.375C1.465 11.375 0.625 10.535 0.625 9.5C0.625 8.465 1.465 7.625 2.5 7.625H4C4.205 7.625 4.375 7.795 4.375 8V9.5C4.375 10.535 3.535 11.375 2.5 11.375ZM2.5 8.375C1.88 8.375 1.375 8.88 1.375 9.5C1.375 10.12 1.88 10.625 2.5 10.625C3.12 10.625 3.625 10.12 3.625 9.5V8.375H2.5Z" fill="#4B5563" stroke="#4B5563" stroke-width="0.2" /><path d="M4 4.375H2.5C1.465 4.375 0.625 3.535 0.625 2.5C0.625 1.465 1.465 0.625 2.5 0.625C3.535 0.625 4.375 1.465 4.375 2.5V4C4.375 4.205 4.205 4.375 4 4.375ZM2.5 1.375C1.88 1.375 1.375 1.88 1.375 2.5C1.375 3.12 1.88 3.625 2.5 3.625H3.625V2.5C3.625 1.88 3.12 1.375 2.5 1.375Z" fill="#4B5563" stroke="#4B5563" stroke-width="0.2" /><path d="M9.5 4.375H8C7.795 4.375 7.625 4.205 7.625 4V2.5C7.625 1.465 8.465 0.625 9.5 0.625C10.535 0.625 11.375 1.465 11.375 2.5C11.375 3.535 10.535 4.375 9.5 4.375ZM8.375 3.625H9.5C10.12 3.625 10.625 3.12 10.625 2.5C10.625 1.88 10.12 1.375 9.5 1.375C8.88 1.375 8.375 1.88 8.375 2.5V3.625Z" fill="#4B5563" stroke="#4B5563" stroke-width="0.2" /><path d="M9.5 11.375C8.465 11.375 7.625 10.535 7.625 9.5V8C7.625 7.795 7.795 7.625 8 7.625H9.5C10.535 7.625 11.375 8.465 11.375 9.5C11.375 10.535 10.535 11.375 9.5 11.375ZM8.375 8.375V9.5C8.375 10.12 8.88 10.625 9.5 10.625C10.12 10.625 10.625 10.12 10.625 9.5C10.625 8.88 10.12 8.375 9.5 8.375H8.375Z" fill="#4B5563" stroke="#4B5563" stroke-width="0.2" /></svg>} iconPosition="start" />
              </Tabs>
            </div> */}
            <div className='right-area'>
              <label>
                <Switch
                  checked={workflowData.status === "active"}
                  onChange={(e) => handleChange("status", e['target']['checked'] ? "active" : "inactive")}
                />
              </label>
              {saving ? <button className='btn-style-new' disabled style={{ cursor: 'wait' }}> Saving... </button>
                : !isDirty ? <button className='btn-style-new btn-saved' disabled> ✓ Saved </button>
                  : (
                    <Tooltip title={'Save Changes'}>
                      <button
                        disabled={saving}
                        className={`btn-style-new`}
                        onClick={() => { console.log('Save Workflow button clicked!'); handleSave(); }}
                      >
                        Save
                      </button>
                    </Tooltip>
                  )}
              <Tooltip title="Executions">
                <Tabs value={viewMode} onChange={(e, value) => setViewMode(value)}>
                  <Tab label="" value="executions" icon={<svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path opacity="0.5" d="M12 8V12L14.5 14.5" stroke="#1C274C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M5.60414 5.60414L5.07381 5.07381V5.07381L5.60414 5.60414ZM4.33776 6.87052L3.58777 6.87429C3.58984 7.28556 3.92272 7.61844 4.33399 7.62051L4.33776 6.87052ZM6.87954 7.6333C7.29375 7.63539 7.63122 7.30129 7.6333 6.88708C7.63538 6.47287 7.30129 6.1354 6.88708 6.13332L6.87954 7.6333ZM5.07496 4.3212C5.07288 3.90699 4.73541 3.5729 4.3212 3.57498C3.90699 3.57706 3.5729 3.91453 3.57498 4.32874L5.07496 4.3212ZM3.82661 10.7849C3.88286 10.3745 3.59578 9.99627 3.1854 9.94002C2.77503 9.88377 2.39675 10.1708 2.3405 10.5812L3.82661 10.7849ZM18.8622 5.13777C15.042 1.31758 8.86873 1.27889 5.07381 5.07381L6.13447 6.13447C9.33358 2.93536 14.5571 2.95395 17.8016 6.19843L18.8622 5.13777ZM5.13777 18.8622C8.95796 22.6824 15.1313 22.7211 18.9262 18.9262L17.8655 17.8655C14.6664 21.0646 9.44291 21.0461 6.19843 17.8016L5.13777 18.8622ZM18.9262 18.9262C22.7211 15.1313 22.6824 8.95796 18.8622 5.13777L17.8016 6.19843C21.0461 9.44291 21.0646 14.6664 17.8655 17.8655L18.9262 18.9262ZM5.07381 5.07381L3.80743 6.34019L4.86809 7.40085L6.13447 6.13447L5.07381 5.07381ZM4.33399 7.62051L6.87954 7.6333L6.88708 6.13332L4.34153 6.12053L4.33399 7.62051ZM5.08775 6.86675L5.07496 4.3212L3.57498 4.32874L3.58777 6.87429L5.08775 6.86675ZM2.3405 10.5812C1.93907 13.5099 2.87392 16.5984 5.13777 18.8622L6.19843 17.8016C4.27785 15.881 3.48663 13.2652 3.82661 10.7849L2.3405 10.5812Z" fill="#1C274C" /></svg>} iconPosition="start" />
                </Tabs>
              </Tooltip>
              <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)}>
                <DropdownToggle caret>
                  <svg width="13" height="3" viewBox="0 0 13 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="1.5" cy="1.5" r="1.5" transform="rotate(-90 1.5 1.5)" fill="#4B5563" />
                    <circle cx="6.5" cy="1.5" r="1.5" transform="rotate(-90 6.5 1.5)" fill="#4B5563" />
                    <circle cx="11.5" cy="1.5" r="1.5" transform="rotate(-90 11.5 1.5)" fill="#4B5563" />
                  </svg>
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem index={0} disabled={!isEditMode || saving}>
                    <button className='action-item' onClick={handleTestRun} disabled={!isEditMode || saving}>
                      <i className='icon'>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.59081 12.4134C4.12998 12.4134 3.69248 12.3025 3.30748 12.0809C2.39748 11.5559 1.89581 10.4884 1.89581 9.08253V4.92337C1.89581 3.5117 2.39748 2.45003 3.30748 1.92503C4.21748 1.40003 5.38998 1.4992 6.61498 2.20503L10.2141 4.2817C11.4333 4.98753 12.11 5.95587 12.11 7.00587C12.11 8.05587 11.4391 9.0242 10.2141 9.73003L6.61498 11.8067C5.90915 12.2092 5.22081 12.4134 4.59081 12.4134ZM4.59081 2.4617C4.27581 2.4617 3.99581 2.5317 3.74498 2.67753C3.11498 3.0392 2.77081 3.83837 2.77081 4.92337V9.0767C2.77081 10.1617 3.11498 10.955 3.74498 11.3225C4.37498 11.69 5.23831 11.585 6.17748 11.0425L9.77665 8.96587C10.7158 8.42337 11.235 7.7292 11.235 7.00003C11.235 6.27087 10.7158 5.5767 9.77665 5.0342L6.17748 2.95753C5.60581 2.63087 5.06915 2.4617 4.59081 2.4617Z" fill="#4B5563" />
                        </svg>
                      </i> Test Run
                    </button>
                  </DropdownItem>
                  <DropdownItem index={1}>
                    <button className='action-item' onClick={handleDownloadJSON}>
                      <i className='icon'><DownloadIcon /></i> Download JSON
                    </button>
                  </DropdownItem>
                  <DropdownItem index={4} onClick={handleOpenCloneDialog}>
                    <button className='action-item' onClick={handleOpenCloneDialog}>
                      <i className='icon'><CopyIcon /></i> Clone
                    </button>
                  </DropdownItem>
                  <DropdownItem index={5} onClick={() => setDeleteDialogOpen(true)}>
                    <button className='action-item' onClick={() => setDeleteDialogOpen(true)}>
                      <i className='icon'>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12.25 3.92578C12.2383 3.92578 12.2208 3.92578 12.2033 3.92578C9.11751 3.61661 6.03751 3.49995 2.98668 3.80911L1.79668 3.92578C1.55168 3.94911 1.33585 3.77411 1.31251 3.52911C1.28918 3.28411 1.46418 3.07411 1.70335 3.05078L2.89335 2.93411C5.99668 2.61911 9.14084 2.74161 12.2908 3.05078C12.53 3.07411 12.705 3.28995 12.6817 3.52911C12.6642 3.75661 12.4717 3.92578 12.25 3.92578Z" fill="#4B5563" />
                          <path d="M4.95832 3.33675C4.93499 3.33675 4.91165 3.33675 4.88249 3.33091C4.64915 3.29008 4.48582 3.06258 4.52665 2.82925L4.65499 2.06508C4.74832 1.50508 4.87665 0.729248 6.23582 0.729248H7.76415C9.12915 0.729248 9.25749 1.53425 9.34499 2.07091L9.47332 2.82925C9.51415 3.06841 9.35082 3.29591 9.11749 3.33091C8.87832 3.37175 8.65082 3.20841 8.61582 2.97508L8.48749 2.21675C8.40582 1.70925 8.38832 1.61008 7.76998 1.61008H6.24165C5.62332 1.61008 5.61165 1.69175 5.52415 2.21091L5.38999 2.96925C5.35499 3.18508 5.16832 3.33675 4.95832 3.33675Z" fill="#4B5563" />
                          <path d="M8.87252 13.2708H5.12752C3.09169 13.2708 3.01002 12.1449 2.94586 11.2349L2.56669 5.36075C2.54919 5.12159 2.73586 4.91159 2.97502 4.89409C3.22002 4.88242 3.42419 5.06326 3.44169 5.30242L3.82086 11.1766C3.88502 12.0633 3.90836 12.3958 5.12752 12.3958H8.87252C10.0975 12.3958 10.1209 12.0633 10.1792 11.1766L10.5584 5.30242C10.5759 5.06326 10.7859 4.88242 11.025 4.89409C11.2642 4.91159 11.4509 5.11576 11.4334 5.36075L11.0542 11.2349C10.99 12.1449 10.9084 13.2708 8.87252 13.2708Z" fill="#4B5563" />
                          <path d="M7.96832 10.0625H6.02582C5.78665 10.0625 5.58832 9.86417 5.58832 9.625C5.58832 9.38583 5.78665 9.1875 6.02582 9.1875H7.96832C8.20748 9.1875 8.40582 9.38583 8.40582 9.625C8.40582 9.86417 8.20748 10.0625 7.96832 10.0625Z" fill="#4B5563" />
                          <path d="M8.45835 7.72925H5.54169C5.30252 7.72925 5.10419 7.53091 5.10419 7.29175C5.10419 7.05258 5.30252 6.85425 5.54169 6.85425H8.45835C8.69752 6.85425 8.89585 7.05258 8.89585 7.29175C8.89585 7.53091 8.69752 7.72925 8.45835 7.72925Z" fill="#4B5563" />
                        </svg>
                      </i> Delete
                    </button>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
              <CollaborativePresence
                workflowId={id || currentWorkflowId}
                onUserSelect={(user) => console.log('Selected user:', user)}
              />
            </div>
          </div>
        </div>
        {/* Main Content */}
        <div className='bottom-content-area'>
          <div className='agent-detail-bottom'>
            {/* Configuration Panel */}
            {/* <h4>Workflow Configuration</h4> */}
            {warnings.length > 0 && (
              <div className='configure-form-area'>
                {errors['submit'] && <Alert severity="error" sx={{ mb: 2 }}>{errors.submit}</Alert>}
                {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Workflow saved successfully!</Alert>}
                {warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Workflow Warnings:</Typography>
                    {warnings.map((warning, index) => <Typography key={index} variant="body2" sx={{ ml: 2 }}> • {warning['message']} </Typography>)}
                    <button className='close-warning-btn' onClick={() => setWarnings(false)}>
                      <svg width="12px" height="12px" viewBox="0 0 56.326 56.326" xmlns="http://www.w3.org/2000/svg" fill="var(--primary-color)">
                        <g id="SVGRepo_bgCarrier" stroke-width="0" />
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                        <g id="SVGRepo_iconCarrier"> <path id="Path_14" data-name="Path 14" d="M477.613,422.087l25.6-25.6a1.5,1.5,0,0,0-2.122-2.121l-25.6,25.6-25.6-25.6a1.5,1.5,0,1,0-2.121,2.121l25.6,25.6-25.6,25.6a1.5,1.5,0,0,0,2.121,2.122l25.6-25.6,25.6,25.6a1.5,1.5,0,0,0,2.122-2.122Z" transform="translate(-447.328 -393.924)" fill="var(--primary-color)" /> </g>
                      </svg>
                    </button>
                  </Alert>
                )}
                {/* <div className='row'>
                  <div className='col-lg-6 col-md-12'>
                    <div className='group-form'>
                      <label>Workflow Name</label>
                      <TextField
                        placeholder="Add workflow name"
                        value={workflowData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        error={!!errors.name}
                        helperText={errors.name}
                        fullWidth
                        required
                      />
                    </div>
                  </div>
                  <div className='col-lg-6 col-md-12'>
                    <div className='group-form'>
                      <label>Status</label>
                      <FormControl fullWidth>
                        <Select
                          value={workflowData.status}
                          onChange={(e) => handleChange('status', e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="draft">Draft</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="paused">Paused</MenuItem>
                          <MenuItem value="archived">Archived</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                  <div className='col-12'>
                    <div className='group-form'>
                      <label>Description</label>
                      <TextField
                        placeholder="Add description"
                        value={workflowData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        sx={{ gridColumn: 'span 2' }}
                      />
                    </div>
                  </div>
                </div> */}
              </div>
            )}

            {errors['submit'] && (
              <div className='configure-form-area'>
                {errors['submit'] && <Alert severity="error" sx={{ mb: 2 }}>{errors['submit']}</Alert>}
                {/* <div className='row'>
                  <div className='col-lg-6 col-md-12'>
                    <div className='group-form'>
                      <label>Workflow Name</label>
                      <TextField
                        placeholder="Add workflow name"
                        value={workflowData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        error={!!errors.name}
                        helperText={errors.name}
                        fullWidth
                        required
                      />
                    </div>
                  </div>
                  <div className='col-lg-6 col-md-12'>
                    <div className='group-form'>
                      <label>Status</label>
                      <FormControl fullWidth>
                        <Select
                          value={workflowData.status}
                          onChange={(e) => handleChange('status', e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="draft">Draft</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="paused">Paused</MenuItem>
                          <MenuItem value="archived">Archived</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                  <div className='col-12'>
                    <div className='group-form'>
                      <label>Description</label>
                      <TextField
                        placeholder="Add description"
                        value={workflowData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        sx={{ gridColumn: 'span 2' }}
                      />
                    </div>
                  </div>
                </div> */}
              </div>
            )}

            {/* {saveSuccess > 0 && (
              <div className='configure-form-area'>
                {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Workflow saved successfully!</Alert>}
              </div>
            )} */}

            {/* Workflow Builder */}
            <div className='config-sec'>
              <div className='tabs-area'>
                <div className='tab-content-area'>
                  {/* {viewMode === 'visual' ? (
                    <Paper sx={{ height: '92vh', overflow: 'hidden' }}>
                      <ReactFlowWorkflowBuilder
                        initialNodes={workflowNodes}
                        initialEdges={workflowEdges}
                        workflowId={isEditMode ? id : null}
                        onSave={(data) => {
                          dispatch(setWorkflowNodes(data.nodes));
                          dispatch(setWorkflowEdges(data.edges));
                          // Validate after changes
                          const validation = validateWorkflow({
                            name: workflowData.name,
                            nodes: data.nodes,
                            edges: data.edges,
                            status: workflowData.status
                          });
                          setWarnings(validation.warnings);
                        }}
                        readOnly={false}
                      />
                    </Paper>
                  ) : (
                    <Paper sx={{ p: 3, height: '100%' }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>JSON Configuration</Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={15}
                        value={JSON.stringify({ nodes: workflowNodes, edges: workflowEdges }, null, 2)}
                        onChange={(e) => {
                          try {
                            const data = JSON.parse(e.target.value);
                            if (data.nodes) dispatch(setWorkflowNodes(data.nodes));
                            if (data.edges) dispatch(setWorkflowEdges(data.edges));
                            setErrors({ ...errors, json: null });
                          } catch (err) {
                            setErrors({ ...errors, json: 'Invalid JSON format' });
                          }
                        }}
                        error={!!errors.json}
                        helperText={errors.json}
                        sx={{ fontFamily: 'monospace' }}
                      />
                    </Paper>
                  )} */}

                  {viewMode === 'visual' ? (
                    // --- VISUAL VIEW ---
                    <Paper sx={{ height: '92vh', }}>
                      <ReactFlowWorkflowBuilder
                        initialNodes={(() => {
                          // Enhance nodes with execution data if available
                          if (!latestExecution?.['executedNodes'] || !workflowNodes) return workflowNodes;

                          const enhancedNodes = workflowNodes.map(node => {
                            const executedNode = latestExecution['executedNodes'].find(en => en['nodeId'] === node['id']);
                            if (executedNode) {
                              return {
                                ...node,
                                data: {
                                  ...node['data'],
                                  output: executedNode['output'],
                                  executedAt: executedNode['endTime'],
                                  executionStatus: executedNode['status'],
                                }
                              }
                            }
                            return node;
                          })

                          return enhancedNodes;
                        })()}
                        initialEdges={workflowEdges}
                        workflowId={isEditMode ? (id || currentWorkflowId || singleWorkflow?._id) : null}
                        onSave={(data) => {
                          dispatch(setWorkflowNodes(data['nodes']));
                          dispatch(setWorkflowEdges(data['edges']));

                          // Validate after changes
                          const validation = validateWorkflow({ nodes: data['nodes'], edges: data['edges'], name: workflowData['name'], status: workflowData['status'] });
                          setWarnings(validation['warnings']);
                        }}
                        onSaveWorkflow={handleSave}
                        saving={saving}
                        saveSuccess={saveSuccess}
                        isDirty={isDirty}
                        setIsDirty={setIsDirty}
                        readOnly={false}
                        setAdvanceSetting={setAdvanceSetting}
                        advanceSetting={advanceSetting}
                      />

                      {/* Advanced Settings */}
                      {advanceSetting && (
                        <div className='config-sec style-advance-settings'>
                          <button className='close-icon' onClick={() => setAdvanceSetting(prev => !prev)}>
                            <svg width="12px" height="12px" viewBox="0 0 56.326 56.326" xmlns="http://www.w3.org/2000/svg" fill="var(--primary-color)">
                              <g id="SVGRepo_bgCarrier" stroke-width="0" />
                              <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                              <g id="SVGRepo_iconCarrier"> <path id="Path_14" data-name="Path 14" d="M477.613,422.087l25.6-25.6a1.5,1.5,0,0,0-2.122-2.121l-25.6,25.6-25.6-25.6a1.5,1.5,0,1,0-2.121,2.121l25.6,25.6-25.6,25.6a1.5,1.5,0,0,0,2.121,2.122l25.6-25.6,25.6,25.6a1.5,1.5,0,0,0,2.122-2.122Z" transform="translate(-447.328 -393.924)" fill="var(--primary-color)" /> </g>
                            </svg>
                          </button>
                          <h4>Advanced Settings</h4>
                          <div className='configure-form-area'>
                            <div className='row'>
                              <div className='col-lg-4 col-md-6 col-sm-12'>
                                <div className='group-form'>
                                  <label>Max Execution Time (ms)</label>
                                  <TextField
                                    placeholder="Add max execution time (ms)"
                                    type="number"
                                    value={workflowData.settings.maxExecutionTime}
                                    onChange={(e) => handleSettingChange('maxExecutionTime', parseInt(e.target.value))}
                                    fullWidth
                                  />
                                </div>
                              </div>
                              <div className='col-lg-4 col-md-6 col-sm-12'>
                                <div className='group-form'>
                                  <label>Max Retries</label>
                                  <TextField
                                    placeholder="Add max retries"
                                    type="number"
                                    value={workflowData.settings.retryPolicy.maxRetries}
                                    onChange={(e) => handleSettingChange('retryPolicy', {
                                      ...workflowData.settings.retryPolicy,
                                      maxRetries: parseInt(e.target.value)
                                    })}
                                    fullWidth
                                  />
                                </div>
                              </div>
                              <div className='col-lg-4 col-md-6 col-sm-12'>
                                <div className='group-form'>
                                  <label>Error Handling</label>
                                  <FormControl fullWidth>
                                    <Select
                                      value={workflowData.settings.errorHandling}
                                      onChange={(e) => handleSettingChange('errorHandling', e.target.value)}
                                      label="Error Handling"
                                    >
                                      <MenuItem value="stop">Stop on Error</MenuItem>
                                      <MenuItem value="continue">Continue on Error</MenuItem>
                                      <MenuItem value="fallback">Use Fallback</MenuItem>
                                    </Select>
                                  </FormControl>
                                </div>
                              </div>
                            </div>
                            <div className='row mt-3'>
                              <div className='col-lg-4 col-md-6 col-sm-12'>
                                <div className='group-form'>
                                  <label>Max Execution Logs</label>
                                  <TextField
                                    placeholder="Max logs to keep (1-15)"
                                    type="number"
                                    inputProps={{ min: 1, max: 15 }}
                                    value={workflowData.settings.executionLogRetention?.maxLogs || 10}
                                    onChange={(e) => {
                                      const value = Math.min(15, Math.max(1, parseInt(e.target.value) || 10));
                                      handleSettingChange('executionLogRetention', {
                                        ...workflowData.settings.executionLogRetention,
                                        maxLogs: value
                                      });
                                    }}
                                    fullWidth
                                  />
                                </div>
                              </div>
                              <div className='col-lg-4 col-md-6 col-sm-12'>
                                <div className='group-form'>
                                  <label>Log Retention Type</label>
                                  <FormControl fullWidth>
                                    <Select
                                      value={workflowData.settings.executionLogRetention?.logType || 'default'}
                                      onChange={(e) => handleSettingChange('executionLogRetention', {
                                        ...workflowData.settings.executionLogRetention,
                                        logType: e.target.value
                                      })}
                                      label="Log Retention Type"
                                    >
                                      <MenuItem value="default">All Logs (Latest)</MenuItem>
                                      <MenuItem value="successful">Successful Only</MenuItem>
                                      <MenuItem value="failed">Failed Only</MenuItem>
                                    </Select>
                                  </FormControl>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Paper>
                  ) : viewMode === 'json' ? (
                    // --- JSON VIEW ---
                    <Paper sx={{ p: 3, height: '100%' }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>JSON Configuration</Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={15}
                        error={!!errors['json']}
                        helperText={errors['json']}
                        sx={{ fontFamily: 'monospace' }}
                        value={JSON.stringify({ nodes: workflowNodes, edges: workflowEdges }, null, 2)}
                        onChange={(e) => {
                          try {
                            const data = JSON.parse(e.target.value);
                            if (data.nodes) dispatch(setWorkflowNodes(data.nodes));
                            if (data.edges) dispatch(setWorkflowEdges(data.edges));
                            setErrors({ ...errors, json: null });
                          } catch (err) {
                            setErrors({ ...errors, json: 'Invalid JSON format' });
                          }
                        }}
                      />
                    </Paper>
                  ) : viewMode === 'executions' ? <WorkflowDetail setViewMode={setViewMode} workflowId={id || currentWorkflowId} /> : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <Modal isOpen={deleteDialogOpen} toggle={() => setDeleteDialogOpen(false)} className={`main-modal new-user-modal modal-dialog-centered workflow-modal`}>
          <ModalHeader toggle={() => setDeleteDialogOpen(false)}>
            <h3>Delete Workflow</h3>
          </ModalHeader>
          <ModalBody>
            <div className='content-area'>
              <div className='form-area'>
                <p>Are you sure you want to delete "{singleWorkflow.name}"?</p>
                <p>This action cannot be undone.</p>
                <div className='btns-box text-right'>
                  <button className='btn-style-border' onClick={() => setDeleteDialogOpen(false)}>Cancel</button>
                  <button className='btn-style-new' onClick={handleDelete} >Delete</button>
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>

        {/* Clone Confirmation Dialog */}
        <Modal isOpen={cloneDialogOpen} toggle={() => setCloneDialogOpen(false)} className={`main-modal new-user-modal modal-dialog-centered workflow-modal`}>
          <ModalHeader toggle={() => setCloneDialogOpen(false)}>
            <h3>Clone Workflow</h3>
          </ModalHeader>
          <ModalBody>
            <div className="content-area">
              <div className="form-area">
                <p>Enter name for the cloned workflow:</p>
                <input
                  type="text"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  className="form-control"
                  disabled={loadingExecution}
                />
                <div className="btns-box text-right mt-3">
                  <button
                    className="btn-style-border"
                    onClick={() => setCloneDialogOpen(false)}
                    disabled={loadingExecution}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-style-new"
                    onClick={handleClone}
                    disabled={loadingExecution || !cloneName?.trim()}
                  >
                    {loadingExecution ? 'Cloning...' : 'Clone'}
                  </button>
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>

        {/* Tags Popover */}
        <Popover
          open={tagsOpen}
          anchorEl={tagsAnchorEl}
          onClose={handleTagsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <TagsManager
            objectType="workflows"
            mode="select"
            selectedObjects={[id || currentWorkflowId]}
            selectedTags={workflowData.tags || []}
            onTagsApplied={handleTagsApplied}
          />
        </Popover>
      </div>
    </>
  )
}

export default WorkflowBuilder
